import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  MemberAddress,
  MemberAddressInput,
  VirtualOfficeAddress,
  VirtualOfficeMine,
  VirtualOfficeNotice,
  VirtualOfficeSettings
} from '@/types/virtualOffice'

/**
 * F-245: virtual office. Paths are written by hand (F-03). The requests on a
 * letter (pick up, forward, discard) live in `ParcelsResource`.
 */
export class VirtualOfficeResource {
  constructor(private ctx: SdkContext) {}

  /** The team reads it (owner, admin, moderator, finance); a member gets 403. */
  async getSettings(): Promise<VirtualOfficeSettings> {
    return this.ctx.call({ httpMethod: 'GET', path: '/virtual-office/settings' })
  }

  /** Owner and admin. `enabled: true` needs the address complete (stored or sent now). */
  async updateSettings(body: { enabled?: boolean; address?: Partial<VirtualOfficeAddress> | null }): Promise<VirtualOfficeSettings> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/virtual-office/settings', hasBody: true }, { body })
  }

  /** "Seu endereço comercial" of the caller. */
  async mine(): Promise<VirtualOfficeMine> {
    return this.ctx.call({ httpMethod: 'GET', path: '/virtual-office/me' })
  }

  /** The team, before registering a letter. Exactly one of `memberId` and `companyId`. */
  async recipientStatus(query: { memberId?: string; companyId?: string }): Promise<VirtualOfficeNotice> {
    return this.ctx.call({ httpMethod: 'GET', path: '/virtual-office/recipient-status' }, { query })
  }

  async listAddresses(): Promise<MemberAddress[]> {
    return listItems<MemberAddress>(await this.ctx.call({ httpMethod: 'GET', path: '/virtual-office/me/addresses' }))
  }

  async createAddress(body: MemberAddressInput): Promise<MemberAddress> {
    return this.ctx.call({ httpMethod: 'POST', path: '/virtual-office/me/addresses', hasBody: true }, { body })
  }

  async updateAddress(addressId: string, body: MemberAddressInput): Promise<MemberAddress> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/virtual-office/me/addresses/{addressId}', hasBody: true }, { addressId, body })
  }

  /** A pending forward keeps its own copy of the address. */
  async removeAddress(addressId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/virtual-office/me/addresses/{addressId}' }, { addressId })
  }
}
